import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useListContracts } from "@workspace/api-client-react";
import { CountUp, HoverLift } from "@/components/motion";
import { MaskedValue } from "@/components/MaskedValue";
import { useOrg } from "@/lib/orgContext";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Plus, Search, Filter, FileText, Download, CalendarRange, Paperclip, AlertTriangle, ArrowUpFromLine, FileDown, ScrollText, FileArchive, FileSignature, FileSpreadsheet, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { EmptyState } from "@/components/ui/empty-state";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { type Plan } from "@workspace/plans";
import { SampleBadge } from "@/components/SampleBadge";
import { LifecycleStateBadge } from "@/components/LifecycleStateBadge";
import { WorkflowStageBadge } from "@/components/WorkflowStageBadge";
import { TermSheetBadge, type TermSheetStatus } from "@/components/TermSheetBadge";
import type { ListContractsDocumentKind } from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";

// Mirrors server BULK_DOWNLOAD_MAX (contracts.ts).
const BULK_DOWNLOAD_MAX = 100;

async function triggerDownload(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

function contractStatusVariant(status: string): "default" | "secondary" | "outline" | "destructive" | "green" | "gold" | "red" | "muted" {
  if (status === "active") return "green";
  if (status === "pending") return "gold";
  if (status === "expired") return "red";
  return "muted";
}

type CfDef = { id: number; key: string; label: string; type: string };

export default function Contracts() {
  const [location, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [importSource, setImportSource] = useState<string>("");
  // Mounted at both /contracts and /term-sheets; mode derived from path.
  // The two routes are fully separated — no Type filter, no "all" branch.
  const isTermSheetMode = location.startsWith("/term-sheets");
  const documentKind: "contract" | "term_sheet" = isTermSheetMode ? "term_sheet" : "contract";

  // Reset incompatible status when crossing /contracts <-> /term-sheets.
  const CONTRACT_STATUSES = new Set(["active", "pending", "expired", "terminated"]);
  const TS_STATUSES = new Set(["draft", "internally_approved", "sent", "accepted", "countered", "declined"]);
  useEffect(() => {
    if (!status || status === "all") return;
    if (isTermSheetMode && CONTRACT_STATUSES.has(status)) setStatus("all");
    if (!isTermSheetMode && TS_STATUSES.has(status)) setStatus("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTermSheetMode]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkJob, setBulkJob] = useState<{
    jobId: string;
    status: "pending" | "running" | "ready" | "failed" | "cancelled";
    processed: number;
    total: number;
    failed: number;
    startedAt: number;
  } | null>(null);
  const bulkCancelRef = useRef(false);
  const isBulkDownloading = bulkJob !== null && bulkJob.status !== "ready" && bulkJob.status !== "failed" && bulkJob.status !== "cancelled";
  const { toast } = useToast();
  const { org } = useOrg();
  const { getToken } = useAuth();
  const isAdmin = org?.role === "admin";
  const currentPlan: Plan = (org?.plan as Plan | undefined) ?? "free";

  // TS mode: skip the contract-only API status filter, filter client-side instead.
  const apiStatus =
    isTermSheetMode || status === "" || status === "all"
      ? undefined
      : (status as import("@workspace/api-client-react").ListContractsStatus);

  // Custom field definitions and batch values for list columns
  const [cfDefs, setCfDefs] = useState<CfDef[]>([]);
  const [cfValues, setCfValues] = useState<Record<number, Record<string, unknown>>>({});

  useEffect(() => {
    let cancelled = false;
    async function fetchCfDefs() {
      const token = await getToken();
      const res = await fetch("/api/custom-fields/definitions?entityType=contract", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const defs = (await res.json()) as CfDef[];
      if (!cancelled) setCfDefs(defs.filter((d: CfDef & { enabled?: boolean }) => d.enabled !== false));
    }
    fetchCfDefs();
    return () => { cancelled = true; };
  }, [getToken]);

  const { data: rawContracts, isLoading } = useListContracts({
    search: search || undefined,
    status: apiStatus,
    importSource: !importSource || importSource === "all"
      ? undefined
      : (importSource as import("@workspace/api-client-react").ListContractsImportSource),
    documentKind: documentKind as ListContractsDocumentKind,
  });

  const contracts =
    isTermSheetMode && status && status !== "all"
      ? (rawContracts ?? []).filter(
          (c) => ((c as { termSheetStatus?: string | null }).termSheetStatus ?? "draft") === status,
        )
      : rawContracts;

  useEffect(() => {
    if (!contracts || contracts.length === 0 || cfDefs.length === 0) return;
    let cancelled = false;
    async function fetchBatch() {
      const token = await getToken();
      const ids = (contracts ?? []).map((c) => c.id).join(",");
      const res = await fetch(`/api/custom-fields/values/batch?entityType=contract&entityIds=${ids}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const data = (await res.json()) as { definitions: CfDef[]; values: Record<number, Record<string, unknown>> };
      if (!cancelled) setCfValues(data.values ?? {});
    }
    fetchBatch();
    return () => { cancelled = true; };
  }, [contracts, cfDefs, getToken]);

  const isViewer = org?.role === "viewer";

  const totalEstimatedBuyout = (contracts ?? []).reduce(
    (sum, c) => sum + (c.status === "active" && typeof c.estimatedBuyoutToday === "number" ? c.estimatedBuyoutToday : 0),
    0,
  );
  const activeWithBuyoutCount = (contracts ?? []).filter(
    (c) => c.status === "active" && typeof c.estimatedBuyoutToday === "number",
  ).length;
  const totalBonuses = (contracts ?? []).reduce((sum, c) => sum + (typeof c.bonusTotal === "number" ? c.bonusTotal : 0), 0);
  const totalAllowances = (contracts ?? []).reduce((sum, c) => sum + (typeof c.allowanceTotal === "number" ? c.allowanceTotal : 0), 0);
  const financialsMasked = (contracts ?? []).some((c) => (c as { financialsMasked?: boolean }).financialsMasked);

  const buildExportFilename = (
    isTermSheet: boolean,
    coachName: string,
    contractId: string | number,
    suffix: "contract" | "summary",
  ) => {
    const coachLastName = (coachName?.split(" ").pop() ?? (isTermSheet ? "term-sheet" : "contract"))
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-");
    const kind = isTermSheet ? "term-sheet" : "contract";
    const date = new Date().toISOString().split("T")[0];
    return suffix === "summary"
      ? `${coachLastName}-${date}-${kind}-summary-${contractId}.pdf`
      : `${coachLastName}-${date}-${kind}-${contractId}.pdf`;
  };

  const handleExportContractPdf = async (
    contractId: string | number,
    coachName: string,
    isTermSheet = false,
  ) => {
    try {
      const filename = buildExportFilename(isTermSheet, coachName, contractId, "contract");
      await triggerDownload(`/api/contracts/${contractId}/export.pdf`, filename);
    } catch {
      toast({
        title: "Export failed",
        description: `Could not generate the ${isTermSheet ? "term sheet" : "contract"} PDF. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleExportContractSummary = async (
    contractId: string | number,
    coachName: string,
    isTermSheet = false,
  ) => {
    try {
      const filename = buildExportFilename(isTermSheet, coachName, contractId, "summary");
      await triggerDownload(`/api/contracts/${contractId}/summary.pdf`, filename);
    } catch {
      toast({
        title: "Export failed",
        description: `Could not generate the ${isTermSheet ? "term sheet" : "contract"} summary PDF. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const allContractIds = (contracts ?? []).map((c) => c.id as number);
  const selectableIds = allContractIds.slice(0, BULK_DOWNLOAD_MAX);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const exceedsBulkDownloadCap = selectedIds.size > BULK_DOWNLOAD_MAX;
  const wouldTruncateSelectAll = allContractIds.length > BULK_DOWNLOAD_MAX;

  const toggleOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    // Cap select-all at BULK_DOWNLOAD_MAX (server-enforced).
    setSelectedIds(new Set(selectableIds));
    if (wouldTruncateSelectAll) {
      const noun = isTermSheetMode ? "term sheets" : "contracts";
      toast({
        title: `Selected the first ${BULK_DOWNLOAD_MAX} ${noun}`,
        description: `Bulk downloads are capped at ${BULK_DOWNLOAD_MAX} ${noun} at a time. Filter the list to narrow your selection.`,
      });
    }
  };
  const clearSelection = () => setSelectedIds(new Set());

  const cancelBulkDownload = async () => {
    bulkCancelRef.current = true;
    const job = bulkJob;
    if (!job || !job.jobId) {
      // Pre-jobId cancel: polling loop will DELETE once the id arrives.
      if (job) setBulkJob({ ...job, status: "cancelled" });
      return;
    }
    try {
      await fetch(`/api/contracts/bulk-download/jobs/${encodeURIComponent(job.jobId)}`, {
        method: "DELETE",
      });
    } catch {
      /* best-effort cancel */
    }
    setBulkJob({ ...job, status: "cancelled" });
    setTimeout(() => setBulkJob(null), 1500);
  };

  const handleBulkDownload = async (includeSummary: boolean) => {
    if (selectedIds.size === 0) return;
    if (bulkJob && bulkJob.status !== "ready" && bulkJob.status !== "failed" && bulkJob.status !== "cancelled") return;
    if (selectedIds.size > BULK_DOWNLOAD_MAX) {
      const noun = isTermSheetMode ? "term sheets" : "contracts";
      toast({
        title: `Too many ${noun} selected`,
        description: `Bulk downloads are capped at ${BULK_DOWNLOAD_MAX} ${noun} at a time. You currently have ${selectedIds.size} selected — please narrow the selection and try again.`,
        variant: "destructive",
      });
      return;
    }
    bulkCancelRef.current = false;
    const expectedTotal = selectedIds.size;
    const startedAt = Date.now();
    setBulkJob({ jobId: "", status: "pending", processed: 0, total: expectedTotal, failed: 0, startedAt });
    try {
      const res = await fetch("/api/contracts/bulk-download/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractIds: Array.from(selectedIds),
          includeSummary,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(errBody?.error ?? "Bulk download failed");
      }
      const created = (await res.json()) as {
        jobId: string;
        status: "pending" | "running" | "ready" | "failed" | "cancelled";
        processed: number;
        total: number;
        failed: number;
      };
      // User cancelled before POST returned — stop the new job.
      if (bulkCancelRef.current) {
        try {
          await fetch(
            `/api/contracts/bulk-download/jobs/${encodeURIComponent(created.jobId)}`,
            { method: "DELETE" },
          );
        } catch {
          /* best-effort */
        }
        setBulkJob({
          jobId: created.jobId,
          status: "cancelled",
          processed: created.processed,
          total: created.total,
          failed: created.failed,
          startedAt,
        });
        setTimeout(() => setBulkJob(null), 1500);
        return;
      }
      setBulkJob({
        jobId: created.jobId,
        status: created.status,
        processed: created.processed,
        total: created.total,
        failed: created.failed,
        startedAt,
      });

      // Poll for progress until the job is ready, failed, or cancelled.
      let final: typeof created | null = null;
      while (true) {
        if (bulkCancelRef.current) return;
        await new Promise((r) => setTimeout(r, 1000));
        if (bulkCancelRef.current) return;
        const sres = await fetch(
          `/api/contracts/bulk-download/jobs/${encodeURIComponent(created.jobId)}`,
        );
        if (!sres.ok) throw new Error("Lost track of bulk download job");
        const snap = (await sres.json()) as typeof created & { error: string | null; fileReady: boolean };
        setBulkJob({
          jobId: snap.jobId,
          status: snap.status,
          processed: snap.processed,
          total: snap.total,
          failed: snap.failed,
          startedAt,
        });
        if (snap.status === "ready") {
          final = snap;
          break;
        }
        if (snap.status === "failed" || snap.status === "cancelled") {
          throw new Error(snap.error ?? "Bulk download failed");
        }
      }

      if (!final) return;

      const fileRes = await fetch(
        `/api/contracts/bulk-download/jobs/${encodeURIComponent(final.jobId)}/file`,
      );
      if (!fileRes.ok) throw new Error("Bulk download file unavailable");
      const blob = await fileRes.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${isTermSheetMode ? "term-sheets" : "contracts"}-${new Date().toISOString().split("T")[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);

      const skipped = final.total - (final.processed - final.failed);
      const skippedNote = final.failed > 0 ? ` (${final.failed} skipped)` : skipped > 0 ? ` (${skipped} skipped)` : "";
      const completed = final.processed - final.failed;
      const itemNoun = isTermSheetMode
        ? `term sheet${completed === 1 ? "" : "s"}`
        : `contract${completed === 1 ? "" : "s"}`;
      toast({
        title: "Download ready",
        description: `Zipped ${completed} ${itemNoun}${includeSummary ? " with summaries" : ""}${skippedNote}.`,
      });
      setTimeout(() => setBulkJob(null), 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not generate the zip. Please try again.";
      toast({
        title: "Bulk download failed",
        description: message,
        variant: "destructive",
      });
      setBulkJob((prev) => (prev ? { ...prev, status: "failed" } : prev));
      setTimeout(() => setBulkJob(null), 1500);
    }
  };

  const handleExport = async (format: "csv" | "xlsx") => {
    setIsExporting(true);
    try {
      const date = new Date().toISOString().split("T")[0];
      // Pass documentKind explicitly for both modes so exports honour the strict
      // tab split — without ?documentKind=contract the server defaults to all
      // kinds and would leak term sheets into the contracts CSV.
      const kind = isTermSheetMode ? "term_sheet" : "contract";
      const fmt = format === "xlsx" ? "&format=xlsx" : "";
      const url = `/api/export/contracts.csv?documentKind=${kind}${fmt}`;
      const filename = isTermSheetMode
        ? `term-sheets-${date}.${format}`
        : `contracts-${date}.${format}`;
      await triggerDownload(url, filename);
    } catch {
      toast({
        title: "Export failed",
        description: isTermSheetMode
          ? "Unable to download term sheets export."
          : "Unable to download contracts export.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>{isTermSheetMode ? "Term Sheets" : "Contracts"}</h1>
          <p>
            {isTermSheetMode
              ? "Draft, share, and promote non-binding term sheets before formalizing into a contract."
              : "Manage and track all coaching contracts across your department."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setLocation("/timeline")}>
            <CalendarRange className="h-3.5 w-3.5 mr-1.5" />
            Timeline
          </Button>
          {isAdmin && !isTermSheetMode && (
            <Button variant="outline" size="sm" onClick={() => setLocation("/contracts/import")}>
              <ArrowUpFromLine className="h-3.5 w-3.5 mr-1.5" />
              Import
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting}
                data-testid={isTermSheetMode ? "button-export-term-sheets-csv" : "button-export-contracts-csv"}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {isExporting
                  ? "Exporting…"
                  : isTermSheetMode
                    ? "Export Term Sheets"
                    : "Export Contracts"}
                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("csv")}>
                <FileText className="h-3.5 w-3.5 mr-2" />
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && isTermSheetMode && (
            <Button onClick={() => setLocation("/term-sheets/new")} data-testid="button-new-term-sheet">
              <FileSignature className="h-3.5 w-3.5 mr-1.5" />
              New Term Sheet
            </Button>
          )}
          {isAdmin && !isTermSheetMode && (
            <Button onClick={() => setLocation("/contracts/new")}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New Contract
            </Button>
          )}
        </div>
      </div>

      {/* Contract-only KPIs (hidden in term-sheet mode). */}
      {!isTermSheetMode && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-children">
        <HoverLift>
          <Card className="card-lift kpi-card-red h-full">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wide font-semibold">
                Total Estimated Buyout Exposure
              </CardDescription>
              <CardTitle className="text-3xl mt-1 kpi-value font-extrabold tracking-tight" data-testid="text-total-buyout">
                {isLoading ? "—" : financialsMasked ? <MaskedValue /> : (
                  <><span className="text-xl mr-0.5">$</span><CountUp value={Math.round(totalEstimatedBuyout)} /></>
                )}
              </CardTitle>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Estimated cost if all {activeWithBuyoutCount} active contract{activeWithBuyoutCount === 1 ? "" : "s"} terminated today — Without Cause (by University), no mitigation applied.
              </div>
            </CardHeader>
          </Card>
        </HoverLift>

        <HoverLift>
          <Card className="card-lift kpi-card-gold h-full">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wide font-semibold">
                Total Bonuses
              </CardDescription>
              <CardTitle className="text-3xl mt-1 kpi-value font-extrabold tracking-tight" data-testid="text-total-bonuses">
                {isLoading ? "—" : financialsMasked ? <MaskedValue /> : (
                  <><span className="text-xl mr-0.5">$</span><CountUp value={Math.round(totalBonuses)} /></>
                )}
              </CardTitle>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Sum of all bonus line items across listed contracts.
              </div>
            </CardHeader>
          </Card>
        </HoverLift>

        <HoverLift>
          <Card className="card-lift kpi-card-blue h-full">
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase tracking-wide font-semibold">
                Total Allowances
              </CardDescription>
              <CardTitle className="text-3xl mt-1 kpi-value font-extrabold tracking-tight" data-testid="text-total-allowances">
                {isLoading ? "—" : financialsMasked ? <MaskedValue /> : (
                  <><span className="text-xl mr-0.5">$</span><CountUp value={Math.round(totalAllowances)} /></>
                )}
              </CardTitle>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Sum of all allowance line items across listed contracts.
              </div>
            </CardHeader>
          </Card>
        </HoverLift>
      </div>
      )}
      {/* Contracts table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by coach or sport…"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-[180px]">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid={isTermSheetMode ? "select-ts-status" : "select-contract-status"}>
                  <Filter className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  <SelectValue placeholder={isTermSheetMode ? "All TS Statuses" : "All Statuses"} />
                </SelectTrigger>
                <SelectContent>
                  {isTermSheetMode ? (
                    <>
                      <SelectItem value="all">All TS Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="internally_approved">Internally Approved</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="countered">Countered</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            {/* Import source filter is contract-only. */}
            {!isTermSheetMode && (
              <div className="w-full sm:w-[220px]">
                <Select value={importSource} onValueChange={setImportSource}>
                  <SelectTrigger data-testid="select-import-source">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="pdf_extraction">PDF extraction</SelectItem>
                    <SelectItem value="csv_upload">CSV upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {selectedIds.size > 0 && (
            <div
              className="mb-3 flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
              data-testid="bulk-action-bar"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium">
                  {selectedIds.size}{" "}
                  {isTermSheetMode
                    ? `term sheet${selectedIds.size === 1 ? "" : "s"}`
                    : `contract${selectedIds.size === 1 ? "" : "s"}`}{" "}
                  selected
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSelection}
                    disabled={isBulkDownloading}
                    data-testid="button-clear-selection"
                  >
                    Clear
                  </Button>
                  {exceedsBulkDownloadCap ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* Disabled buttons don't fire pointer events, so wrap
                              in a span to keep the tooltip working. */}
                          <span tabIndex={0} data-testid="button-bulk-download-disabled-wrapper">
                            <Button
                              size="sm"
                              disabled
                              data-testid="button-bulk-download"
                            >
                              <FileArchive className="h-4 w-4 mr-1.5" />
                              Download selected
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          Bulk downloads are limited to {BULK_DOWNLOAD_MAX}{" "}
                          {isTermSheetMode ? "term sheets" : "contracts"} at a time.
                          Clear some selections to continue.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          disabled={isBulkDownloading}
                          data-testid="button-bulk-download"
                        >
                          <FileArchive className="h-4 w-4 mr-1.5" />
                          {isBulkDownloading ? "Preparing zip…" : "Download selected"}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={() => handleBulkDownload(false)}
                          data-testid="bulk-download-pdfs-only"
                        >
                          <FileDown className="h-3.5 w-3.5 mr-2" />
                          {isTermSheetMode ? "Term sheet PDFs only" : "Contract PDFs only"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleBulkDownload(true)}
                          data-testid="bulk-download-with-summaries"
                        >
                          <ScrollText className="h-3.5 w-3.5 mr-2" />
                          {isTermSheetMode ? "Term sheets + summaries" : "Contracts + summaries"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              {exceedsBulkDownloadCap && (
                <div
                  className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
                  data-testid="bulk-download-cap-warning"
                  role="status"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Bulk downloads are capped at {BULK_DOWNLOAD_MAX}{" "}
                    {isTermSheetMode ? "term sheets" : "contracts"} at a time.
                    You have {selectedIds.size} selected — clear {selectedIds.size - BULK_DOWNLOAD_MAX} or
                    narrow the list to download in batches.
                  </span>
                </div>
              )}
              {bulkJob && (
                <div className="space-y-1.5" data-testid="bulk-download-progress">
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span data-testid="bulk-download-progress-text">
                      {bulkJob.status === "pending" && "Queuing bulk download…"}
                      {bulkJob.status === "running" && (
                        <>
                          Preparing {bulkJob.processed} of {bulkJob.total}{" "}
                          {isTermSheetMode
                            ? `term sheet${bulkJob.total === 1 ? "" : "s"}`
                            : `contract${bulkJob.total === 1 ? "" : "s"}`}
                          {(() => {
                            const elapsedMs = Date.now() - bulkJob.startedAt;
                            if (bulkJob.processed < 2 || elapsedMs < 2000) return "";
                            const perItem = elapsedMs / bulkJob.processed;
                            const remaining = Math.max(0, bulkJob.total - bulkJob.processed);
                            const etaSec = Math.round((perItem * remaining) / 1000);
                            if (etaSec <= 0) return "";
                            if (etaSec < 60) return ` · ~${etaSec}s left`;
                            const m = Math.floor(etaSec / 60);
                            const s = etaSec % 60;
                            return ` · ~${m}m ${s}s left`;
                          })()}
                        </>
                      )}
                      {bulkJob.status === "ready" && `Downloading ${bulkJob.processed} of ${bulkJob.total}…`}
                      {bulkJob.status === "failed" && "Bulk download failed"}
                      {bulkJob.status === "cancelled" && "Bulk download cancelled"}
                    </span>
                    {(bulkJob.status === "pending" || bulkJob.status === "running") && (
                      <button
                        type="button"
                        onClick={cancelBulkDownload}
                        className="text-xs font-medium text-primary hover:underline"
                        data-testid="bulk-download-cancel"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  <Progress
                    value={
                      bulkJob.total > 0
                        ? Math.min(
                            100,
                            Math.round(
                              (bulkJob.processed / bulkJob.total) * 100,
                            ),
                          )
                        : 0
                    }
                    className="h-1.5"
                    data-testid="bulk-download-progress-bar"
                  />
                </div>
              )}
            </div>
          )}
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)
            ) : contracts && contracts.length > 0 ? (
              contracts.map((contract) => {
                const isTermSheet = (contract as { documentKind?: string }).documentKind === "term_sheet";
                const detailHref = isTermSheet ? `/term-sheets/${contract.id}` : `/contracts/${contract.id}`;
                const tsStatus = (contract as { termSheetStatus?: string | null }).termSheetStatus;
                const salaryMin = (contract as { salaryRangeMin?: number | null }).salaryRangeMin;
                const salaryMax = (contract as { salaryRangeMax?: number | null }).salaryRangeMax;
                return (
                <div
                  key={contract.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setLocation(detailHref)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setLocation(detailHref);
                    }
                  }}
                  className="w-full text-left rounded-xl border p-4 space-y-2.5 hover:bg-muted/40 active:bg-muted transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                        <Checkbox
                          checked={selectedIds.has(contract.id)}
                          onCheckedChange={(c) => toggleOne(contract.id, c === true)}
                          aria-label={`Select ${contract.coachName}`}
                          data-testid={`checkbox-mobile-${contract.id}`}
                        />
                      </div>
                      <div className="min-w-0">
                      <div className="font-semibold text-sm truncate flex items-center gap-1.5 flex-wrap">
                        <span>{contract.coachName}</span>
                        {contract.isSample && <SampleBadge />}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {contract.sport} · <span className="capitalize">{contract.contractType.replace('_', ' ')}</span>
                      </div>
                      </div>
                    </div>
                    {isTermSheet ? (
                      <TermSheetBadge status={(tsStatus ?? null) as TermSheetStatus | null} />
                    ) : (
                      <Badge variant={contractStatusVariant(contract.status)}>
                        {contract.status}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground mb-0.5">Term</div>
                      {isTermSheet && !contract.startDate ? (
                        <div className="font-medium text-muted-foreground italic">TBD</div>
                      ) : (
                        <>
                          <div className="font-medium">{contract.startDate ? format(new Date(contract.startDate), 'MMM d, yyyy') : "—"}</div>
                          <div className="text-muted-foreground">to {contract.endDate ? format(new Date(contract.endDate), 'MMM d, yyyy') : "—"}</div>
                        </>
                      )}
                    </div>
                    <div>
                      <div className="text-muted-foreground mb-0.5">{isTermSheet ? "Salary range" : "Salary"}</div>
                      <div className="font-medium kpi-value">
                        {isTermSheet ? (
                          (contract as { financialsMasked?: boolean }).financialsMasked
                            ? <MaskedValue />
                            : (typeof salaryMin === "number" || typeof salaryMax === "number")
                              ? `${typeof salaryMin === "number" ? `$${salaryMin.toLocaleString()}` : "—"}${typeof salaryMax === "number" ? ` – $${salaryMax.toLocaleString()}` : ""}`
                              : <span className="text-muted-foreground">TBD</span>
                        ) : typeof contract.annualSalary === "number"
                          ? `$${contract.annualSalary.toLocaleString()}`
                          : <MaskedValue />}
                      </div>
                      {!isTermSheet && (
                        <>
                          <div className="text-muted-foreground mt-1 mb-0.5">Buyout today</div>
                          <div className="font-medium kpi-value">
                            {(contract as { financialsMasked?: boolean }).financialsMasked
                              ? <MaskedValue />
                              : contract.status === "active" && typeof contract.estimatedBuyoutToday === "number"
                              ? `$${Math.round(contract.estimatedBuyoutToday).toLocaleString()}`
                              : "—"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Paperclip className="h-3.5 w-3.5" />
                    {contract.documentCount} doc{contract.documentCount === 1 ? "" : "s"}
                    {!contract.hasExecutedContract && (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
                        <AlertTriangle className="h-3 w-3" />No executed
                      </span>
                    )}
                    <div className={!contract.hasExecutedContract ? "" : "ml-auto"} onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2"
                            aria-label="Download contract"
                            data-testid={`button-download-mobile-${contract.id}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            Download
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem
                            onClick={() => handleExportContractPdf(contract.id, contract.coachName, isTermSheet)}
                          >
                            <FileDown className="h-3.5 w-3.5 mr-2" />
                            Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleExportContractSummary(contract.id, contract.coachName, isTermSheet)}
                          >
                            <ScrollText className="h-3.5 w-3.5 mr-2" />
                            Plain-English Summary
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
                );
              })
            ) : (
              search || (status && status !== "all") ? (
                <EmptyState
                  icon={isTermSheetMode ? ScrollText : FileText}
                  title={isTermSheetMode ? "No term sheets match your filters" : "No contracts match your filters"}
                  description="Try adjusting your search or status filter to find what you're looking for."
                  variant="compact"
                />
              ) : isTermSheetMode ? (
                <EmptyState
                  icon={ScrollText}
                  title="No term sheets yet"
                  description="Term sheets capture headline terms early in a hiring conversation. Draft one to share a non-binding offer with a candidate's representative."
                  action={isAdmin ? { label: "New term sheet", href: "/term-sheets/new" } : undefined}
                />
              ) : (
                <EmptyState
                  icon={FileText}
                  title="No contracts on file yet"
                  description="Import your existing contract repository from PDFs or a spreadsheet, or create a contract manually."
                  action={isAdmin ? { label: "Import existing contracts", href: "/contracts/import" } : undefined}
                  secondaryAction={isAdmin ? { label: "New contract", href: "/contracts/new" } : undefined}
                />
              )
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border overflow-hidden">
            <Table className="min-w-[900px] table-refined">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(c) => toggleAll(c === true)}
                      aria-label={isTermSheetMode ? "Select all term sheets" : "Select all contracts"}
                      data-testid="checkbox-select-all"
                    />
                  </TableHead>
                  <TableHead>Coach</TableHead>
                  <TableHead>Sport / Role</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>{isTermSheetMode ? "Salary Range" : "Annual Salary"}</TableHead>
                  {!isTermSheetMode && (
                    <TableHead title="Sum of all bonus amounts on the contract">Bonuses</TableHead>
                  )}
                  {!isTermSheetMode && (
                    <TableHead title="Estimated buyout if terminated today (Without Cause by University, no mitigation)">
                      Est. Buyout Today
                    </TableHead>
                  )}
                  <TableHead>Docs</TableHead>
                  <TableHead>Status</TableHead>
                  {cfDefs.map((d) => (
                    <TableHead key={d.key} title={d.label} className="max-w-[120px] truncate">{d.label}</TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-4 skeleton-shimmer" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[150px] skeleton-shimmer" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[100px] skeleton-shimmer" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[120px] skeleton-shimmer" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>
                      {!isTermSheetMode && <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>}
                      {!isTermSheetMode && <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>}
                      <TableCell><Skeleton className="h-4 w-[50px] skeleton-shimmer" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-[70px] rounded-md skeleton-shimmer" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-8 w-[60px] ml-auto skeleton-shimmer" /></TableCell>
                    </TableRow>
                  ))
                ) : contracts && contracts.length > 0 ? (
                  contracts.map((contract) => {
                    const isTermSheet = (contract as { documentKind?: string }).documentKind === "term_sheet";
                    const detailHref = isTermSheet ? `/term-sheets/${contract.id}` : `/contracts/${contract.id}`;
                    const tsStatus = (contract as { termSheetStatus?: string | null }).termSheetStatus;
                    const salaryMin = (contract as { salaryRangeMin?: number | null }).salaryRangeMin;
                    const salaryMax = (contract as { salaryRangeMax?: number | null }).salaryRangeMax;
                    return (
                    <TableRow key={contract.id} className="cursor-pointer" data-state={selectedIds.has(contract.id) ? "selected" : undefined} onClick={() => setLocation(detailHref)}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(contract.id)}
                          onCheckedChange={(c) => toggleOne(contract.id, c === true)}
                          aria-label={`Select ${contract.coachName}`}
                          data-testid={`checkbox-${contract.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-semibold">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{contract.coachName}</span>
                          {contract.isSample && <SampleBadge />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">{contract.sport}</span>
                          <span className="text-xs text-muted-foreground capitalize">{contract.contractType.replace('_', ' ')}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {isTermSheet && !contract.startDate ? (
                          <span className="text-xs text-muted-foreground italic">TBD</span>
                        ) : (
                          <div className="flex flex-col text-xs">
                            <span className="font-medium">{contract.startDate ? format(new Date(contract.startDate), 'MMM d, yyyy') : "—"}</span>
                            <span className="text-muted-foreground">to {contract.endDate ? format(new Date(contract.endDate), 'MMM d, yyyy') : "—"}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="kpi-value font-medium">
                        {isTermSheet ? (
                          (contract as { financialsMasked?: boolean }).financialsMasked
                            ? <MaskedValue />
                            : (typeof salaryMin === "number" || typeof salaryMax === "number") ? (
                                <span className="whitespace-nowrap">
                                  {typeof salaryMin === "number" ? `$${salaryMin.toLocaleString()}` : "—"}
                                  {typeof salaryMax === "number" ? ` – $${salaryMax.toLocaleString()}` : ""}
                                </span>
                              )
                              : <span className="text-muted-foreground italic">TBD</span>
                        ) : typeof contract.annualSalary === "number"
                          ? `$${contract.annualSalary.toLocaleString()}`
                          : <MaskedValue />}
                      </TableCell>
                      {!isTermSheetMode && (
                        <TableCell className="kpi-value" data-testid={`text-bonus-total-${contract.id}`}>
                          {isTermSheet ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (contract as { financialsMasked?: boolean }).financialsMasked
                            ? <MaskedValue />
                            : typeof contract.bonusTotal === "number" && contract.bonusTotal > 0
                            ? `$${Math.round(contract.bonusTotal).toLocaleString()}`
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      {!isTermSheetMode && (
                        <TableCell className="kpi-value" data-testid={`text-buyout-today-${contract.id}`}>
                          {isTermSheet ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (contract as { financialsMasked?: boolean }).financialsMasked
                            ? <MaskedValue />
                            : contract.status === "active" && typeof contract.estimatedBuyoutToday === "number"
                            ? `$${Math.round(contract.estimatedBuyoutToday).toLocaleString()}`
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Paperclip className="h-3.5 w-3.5" />
                            {contract.documentCount}
                          </span>
                          {!contract.hasExecutedContract && (
                            <span
                              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md bg-destructive/10 text-destructive border border-destructive/20"
                              title="No executed contract on file"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              Missing
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isTermSheet ? (
                          <TermSheetBadge status={(tsStatus ?? null) as TermSheetStatus | null} />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <Badge variant={contractStatusVariant(contract.status)}>
                              {contract.status}
                            </Badge>
                            <WorkflowStageBadge
                              lifecycleState={(contract as any).lifecycleState ?? "draft"}
                              approvalCleared={(contract as any).approvalCleared ?? false}
                            />
                            <LifecycleStateBadge state={(contract as any).lifecycleState ?? "draft"} size="sm" />
                            {["out_for_negotiation", "counter_received"].includes((contract as any).lifecycleState ?? "") && (
                              <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md border font-medium ${(contract as any).negotiationHolder === "counterparty" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                                {(contract as any).negotiationHolder === "counterparty" ? "With them" : "With us"}
                              </span>
                            )}
                            {["out_for_negotiation", "counter_received"].includes((contract as any).lifecycleState ?? "") && (contract.openIssuesCount ?? 0) > 0 && (
                              <span
                                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md border font-medium bg-amber-50 text-amber-800 border-amber-200"
                                title="Open negotiation issues"
                                data-testid={`badge-open-issues-${contract.id}`}
                              >
                                {contract.openIssuesCount} open
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      {cfDefs.map((d) => {
                        const raw = cfValues[contract.id]?.[d.key];
                        const display = raw == null ? "" : d.type === "checkbox" ? (raw ? "Yes" : "No") : String(raw);
                        return (
                          <TableCell key={d.key} className="max-w-[120px] truncate text-xs text-muted-foreground" title={display}>
                            {display || <span className="italic">—</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="Download contract"
                                data-testid={`button-download-${contract.id}`}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onClick={() => handleExportContractPdf(contract.id, contract.coachName, isTermSheet)}
                                data-testid={`menuitem-export-pdf-${contract.id}`}
                              >
                                <FileDown className="h-3.5 w-3.5 mr-2" />
                                Export PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleExportContractSummary(contract.id, contract.coachName, isTermSheet)}
                                data-testid={`menuitem-export-summary-${contract.id}`}
                              >
                                <ScrollText className="h-3.5 w-3.5 mr-2" />
                                Plain-English Summary
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button variant="ghost" size="sm" asChild>
                            <Link href={detailHref}>View</Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={isTermSheetMode ? 8 + cfDefs.length : 10 + cfDefs.length} className="p-0">
                      {search || (status && status !== "all") ? (
                        <EmptyState
                          icon={isTermSheetMode ? ScrollText : FileText}
                          title={isTermSheetMode ? "No term sheets match your filters" : "No contracts match your filters"}
                          description="Try adjusting your search or status filter to find what you're looking for."
                          variant="compact"
                        />
                      ) : isTermSheetMode ? (
                        <EmptyState
                          icon={ScrollText}
                          title="No term sheets yet"
                          description="Term sheets capture headline terms early in a hiring conversation. Draft one to share a non-binding offer with a candidate's representative."
                          action={isAdmin ? { label: "New term sheet", href: "/term-sheets/new" } : undefined}
                        />
                      ) : (
                        <EmptyState
                          icon={FileText}
                          title="No contracts on file yet"
                          description="Import your existing contract repository from PDFs or a spreadsheet, or create a contract manually."
                          action={isAdmin ? { label: "Import existing contracts", href: "/contracts/import" } : undefined}
                          secondaryAction={isAdmin ? { label: "New contract", href: "/contracts/new" } : undefined}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
